import { useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Layers, TrendingUp, DollarSign, Activity, Loader2, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { InstallmentsList } from "@/components/installments/InstallmentsList";
import { InstallmentsFilters } from "@/components/installments/InstallmentsFilters";
import { MainLayout } from "@/components/MainLayout";
import { SEO } from "@/components/SEO";
import { RouteDisplay } from "@/components/navigation/RouteDisplay";
import { useInstallments } from "@/hooks/useInstallments";
import type { InstallmentSeries } from "@/hooks/useInstallments";
import { toast } from "sonner";

export default function InstallmentsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [filters, setFilters] = useState({
    status: searchParams.get("status") || "all",
    category: searchParams.get("category") || "all",
    type: searchParams.get("type") || "all"
  });

  // Detail modal state
  const [selectedSeries, setSelectedSeries] = useState<InstallmentSeries | null>(null);

  // Map component filters to hook filters
  const hookFilters = useMemo(() => {
    const mappedFilters: any = {};

    if (filters.status !== "all") {
      mappedFilters.status = filters.status;
    }

    if (filters.category !== "all") {
      mappedFilters.category_id = parseInt(filters.category, 10);
    }

    if (filters.type !== "all") {
      mappedFilters.type = filters.type;
    }

    if (searchTerm) {
      mappedFilters.search = searchTerm;
    }

    return mappedFilters;
  }, [searchTerm, filters]);

  // Fetch installment transactions using the hook
  const {
    installmentSeries,
    isLoading,
    error,
    statistics
  } = useInstallments(hookFilters);

  // Handle action handlers
  const handleSeriesClick = (series: InstallmentSeries) => {
    setSelectedSeries(series);
    // TODO: Open detail modal
    toast.info(`Detalhes: ${series.description}`, {
      description: `${series.paid_count}/${series.total_installments} parcelas pagas`
    });
  };

  const handleFiltersChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.status !== "all") params.set("status", newFilters.status);
    if (newFilters.category !== "all") params.set("category", newFilters.category);
    if (newFilters.type !== "all") params.set("type", newFilters.type);
    if (searchTerm) params.set("search", searchTerm);
    navigate({ search: params.toString() }, { replace: true });
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Update URL params
    const params = new URLSearchParams();
    if (filters.status !== "all") params.set("status", filters.status);
    if (filters.category !== "all") params.set("category", filters.category);
    if (filters.type !== "all") params.set("type", filters.type);
    if (value) params.set("search", value);
    navigate({ search: params.toString() }, { replace: true });
  };

  if (error) {
    return (
      <MainLayout>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Erro ao carregar transações parceladas: {error.message}
          </AlertDescription>
        </Alert>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <SEO
        title="Transações Parceladas"
        description="Gerencie suas transações parceladas"
      />
      <RouteDisplay
        title="Transações Parceladas"
        description="Acompanhe e gerencie suas compras e vendas parceladas"
        icon={Layers}
      />

      <div className="space-y-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Layers className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total de Séries</p>
                  <p className="text-2xl font-bold">{statistics.total_series}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Activity className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Ativas</p>
                  <p className="text-2xl font-bold">{statistics.active_series}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <DollarSign className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Valor Total</p>
                  <p className="text-lg font-bold">
                    {new Intl.NumberFormat("pt-BR", {
                      style: "currency",
                      currency: "BRL"
                    }).format(statistics.total_value)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Progresso Geral</p>
                  <p className="text-2xl font-bold">
                    {Math.round(statistics.overall_progress)}%
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Search */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <div className="space-y-4">
              <InstallmentsFilters
                filters={filters}
                onFiltersChange={handleFiltersChange}
              />
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-4">
            {/* Search Bar */}
            <Input
              placeholder="Buscar por descrição..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="max-w-md"
            />

            {/* Installments List */}
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <InstallmentsList
                series={installmentSeries}
                onSeriesClick={handleSeriesClick}
              />
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
